import React, { useState } from 'react'
import { AccordionDetails, Box, Fade, styled } from '@mui/material'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import { StaticImage } from 'gatsby-plugin-image'
import { useTranslation } from 'react-i18next'
import FeaturesMobile from './features-mobile'
import Suisse from '../../../frontend-lib/typhograpy/Suisse'
import SangBleu from '../../../frontend-lib/typhograpy/SangBleu'
import { useIsMd } from '../../../theme/useIsMd'

const Accordion = styled((props) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    {...props}
    TransitionProps={{ timeout: 500 }}
    style={{ background: props.background }}
  />
))(() => ({
  '&:before': {
    display: 'none',
  },
}))

const AccordionSummary = styled((props) => <MuiAccordionSummary {...props} />)(
  () => ({
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  })
)

const getHeader = ({ t, section, isMd }) => {
  const header = {
    home: (
      <SangBleu
        size={isMd ? 56 : 32}
        color="grey.1"
        align="left"
        sx={{
          mb: 11,
          pr: 8,
          width: '100%',
          maxWidth: { md: '1041px', sm: '896px' },
        }}
        component="h2"
      >
        {t(`${section}.features.title`)}
        <span style={{ color: '#6665DD' }}>{t('home.features.title1')}</span>
        {t(`${section}.features.title2`)}
      </SangBleu>
    ),
    banks: (
      <SangBleu
        size={isMd ? 56 : 32}
        color="grey.1"
        align="left"
        sx={{
          mb: 11,
          pr: 8,
          width: '100%',
          maxWidth: { md: '1041px', sm: '896px' },
        }}
        component="h2"
      >
        {t(`banks.features.title`)}
      </SangBleu>
    ),
    insurance: (
      <SangBleu
        size={isMd ? 56 : 32}
        color="grey.1"
        align="left"
        sx={{
          mb: 11,
          pr: 8,
          width: '100%',
          maxWidth: { md: '1041px', sm: '896px' },
        }}
        component="h2"
      >
        {t(`${section}.features.title`)}
        <span style={{ color: '#6665DD' }}>{t('home.features.title1')}</span>
        {t(`${section}.features.title2`)}
      </SangBleu>
    ),
  }
  return header[section]
}

function Features({ section = 'home' }) {
  const [checked, setChecked] = useState({ option: 1 })
  const isMd = useIsMd()
  const { t } = useTranslation()

  const ITEMS = [
    {
      key: 1,
      title: t('home.features.items.dashboard.title'),
      subtitle: t('home.features.items.dashboard.subtitle'),
      img: (
        <StaticImage
          src="../../../../static/images/Dashboard.png"
          alt="dashboard-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 2,
      title: t(`${section}.features.items.money.title`),
      subtitle: t(`${section}.features.items.money.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Money.png"
          alt="money-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 3,
      title: t(`${section}.features.items.money1.title`),
      subtitle: t(`${section}.features.items.money1.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Money1.png"
          alt="money1-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 4,
      title: t(`${section}.features.items.identity.title`),
      subtitle: t(`${section}.features.items.identity.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Identity.png"
          alt="identity-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 5,
      title: t(`${section}.features.items.trusted.title`),
      subtitle: t(`${section}.features.items.trusted.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Trusted.png"
          alt="trusted-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 6,
      title: t(`${section}.features.items.home.title`),
      subtitle: t(`${section}.features.items.home.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Home.png"
          alt="home-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
    {
      key: 7,
      title: t(`${section}.features.items.vault.title`),
      subtitle: t(`${section}.features.items.vault.subtitle`),
      img: (
        <StaticImage
          src="../../../../static/images/Vault.png"
          alt="vault-photo"
          layout="constrained"
          style={{
            width: '100%',
            maxWidth: '1000px',
            height: '100%',
            maxHeight: '750px',
            objectFit: 'contain',
          }}
        />
      ),
    },
  ]

  if (!isMd) {
    return <FeaturesMobile ITEMS={ITEMS} t={t} section={section} />
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: '128px',
        pl: { lg: '128px', md: '64px' },
        pr: { lg: '128px' },
        flex: 1,
        bgcolor: 'mono.white',
        borderTop: '1px solid rgba(31, 36, 45, 0.1)',
      }}
    >
      {getHeader({ t, section, isMd })}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            {ITEMS.map((item) => (
              <Accordion
                expanded={checked.option === item.key}
                onChange={() => setChecked({ option: item.key })}
                key={`accordion-${item.key}`}
                background="#FFFFFF"
                sx={{
                  borderTop:
                    checked.option === item.key
                      ? ' 4px solid #6665DD'
                      : '1px solid #D2D7E0',
                }}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                >
                  <Suisse
                    size={18}
                    type="regular"
                    color="mono.black"
                    sx={{
                      fontSize: checked.option === item.key ? '24px' : '18px',
                      fontWeight: 600,
                      pt: checked.option === item.key ? 2 : 0,
                      pb: checked.option !== item.key ? 2 : 0,
                      transition: 'all 1s',
                    }}
                  >
                    {item.title}
                  </Suisse>
                </AccordionSummary>
                <AccordionDetails>
                  <Suisse
                    size={18}
                    type="regular"
                    color="grey.1"
                    sx={{
                      color: 'grey.1',
                      animation:
                        checked.option === item.key
                          ? 'fadeIn 600ms linear'
                          : '',
                      ' @keyframes fadeIn': {
                        '0%': { opacity: 0 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  >
                    {item.subtitle}
                  </Suisse>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 2,
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
            }}
          >
            {ITEMS.map((item) => (
              <Fade
                in={checked.option === item.key}
                timeout={700}
                key={`fade-${item.key}`}
              >
                {checked.option === item.key ? (
                  <Box
                    key={`img-${item.key}`}
                    sx={{
                      ml: 8,
                      filter: 'drop-shadow(0px 0px 40px rgba(0, 0, 0, 0.1))',
                    }}
                  >
                    {item.img}
                  </Box>
                ) : (
                  <Box />
                )}
              </Fade>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Features
